import googleAnalytics from './googleAnalytics';
import googleTagManager, { GoogleTagManager } from './googleTagManager';
import { QuickPickEventParams, GtagItem, TrackingEventData } from './types';

import { Basket, LineItem } from 'Api/Models/SiteApi/Basket';

import { PopTrackingEventsResponse } from 'Api/SiteApi/Response/AccountApi';
import { roundToDecimals } from 'utils/helpers';

export const GOOGLE_TRACKING_ENABLED = googleAnalytics.isActive || googleTagManager.isActive;

export const trackQuickPickButtonClicked = (lotterySlug: string, lines: number = 1) => {
    if (GOOGLE_TRACKING_ENABLED) {
        const eventParams: QuickPickEventParams = { lotterySlug };
        if (lines > 1) eventParams.lines = lines;
        GoogleTagManager.gtag('event', 'QuickPickButtonClicked', eventParams);
    }
};

const mapBasketLineToGtagItem = (line: LineItem): GtagItem => ({
    item_name: line.product.lottery.name,
    item_variant: line.product.type.replace('DrawLotteryTicket', ''),
    price: Math.round(line.sellPrice.amount / (line.product.numberOfDraws ?? 1)) / 100,
    quantity: line.product.numberOfDraws ?? 1,
    lines: line.product.numberOfLines,
    discount: (line.basePrice.amount - line.sellPrice.amount) / 100,
});

const mapBasketToGtagEventParams = (basket: Basket): Gtag.EventParams => ({
    currency: basket.sellSumGross.currency,
    value: basket.sellSumGross.amount / 100,
    items: basket.lines.map(mapBasketLineToGtagItem),
});

export const trackAddToCart = (
    itemName: string,
    currency: string,
    value: number,
    price: number,
    numberOfDraws: number,
    numberOfLines: number
) => {
    if (GOOGLE_TRACKING_ENABLED) {
        const item: GtagItem = {
            item_name: itemName,
            item_variant: numberOfDraws > 1 ? 'multi' : 'single',
            price: Math.round((price * 100) / numberOfDraws) / 100,
            quantity: numberOfDraws,
            lines: numberOfLines,
            discount: roundToDecimals(value - price),
        };
        GoogleTagManager.gtag('event', 'add_to_cart', { currency, value: roundToDecimals(value), items: [item] });
    }
};

export const trackRemoveFromCart = (line: LineItem) => {
    if (GOOGLE_TRACKING_ENABLED) {
        GoogleTagManager.gtag('event', 'remove_from_cart', {
            currency: line.sellPrice.currency,
            value: line.sellPrice.amount / 100,
            items: [mapBasketLineToGtagItem(line)],
        });
    }
};

export const trackViewCart = (basket: Basket) => {
    if (GOOGLE_TRACKING_ENABLED) {
        GoogleTagManager.gtag('event', 'view_cart', mapBasketToGtagEventParams(basket));
    }
};

export const trackBeginCheckout = (basket: Basket, eventCallback?: () => void) => {
    if (GOOGLE_TRACKING_ENABLED) {
        GoogleTagManager.gtag('event', 'begin_checkout', {
            ...mapBasketToGtagEventParams(basket),
            event_callback: eventCallback,
        });
    } else {
        eventCallback?.();
    }
};

export function prepareTrackingEventsResponse(response: PopTrackingEventsResponse): PopTrackingEventsResponse {
    const { trackingEvents, metaData } = response;

    return { trackingEvents, metaData };
}

/**
 * Prevents duplicate unique events from being sent when they might be generated multiple times due to a page refresh.
 */
export function eventAlreadyProcessedInSession(eventData: TrackingEventData): boolean {
    if (!eventData.transaction_id) {
        return false;
    }

    const KEY_NAME = 'unique_tracking_event_list';
    const eventId = eventData.transaction_id;
    const storedEvents = (sessionStorage.getItem(KEY_NAME) ?? '').split(',').filter(Boolean);
    if (storedEvents.includes(eventId)) {
        return true;
    }

    storedEvents.push(eventId);
    sessionStorage.setItem(KEY_NAME, storedEvents.join(','));

    return false;
}
